// ============================================================================
// Notifications API
// ============================================================================
// Handles storing and retrieving user notifications
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';

/**
 * Get notifications for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Max number to return (default 20)
 * @param {number} options.offset - Number of records to skip (for pagination, default 0)
 * @param {boolean} options.unreadOnly - Only return unread notifications
 * @returns {Promise<{data: Array, error: Error}>}
 */
export async function getNotifications({ limit = 20, offset = 0, unreadOnly = false } = {}) {
    const userId = auth.user?.id;
    if (!userId) return { data: [], error: null };

    let query = supabase
        .from('notifications')
        .select(`
            id,
            type,
            title,
            message,
            data,
            entity_type,
            entity_id,
            is_read,
            created_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

    if (unreadOnly) {
        query = query.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) {
        console.error('[NotificationsAPI] Error fetching notifications:', error);
        return { data: [], error };
    }

    return { data: data || [], error: null };
}

/**
 * Get unread notification count
 * @returns {Promise<number>}
 */
export async function getUnreadCount() {
    const userId = auth.user?.id;
    if (!userId) return 0;

    const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

    if (error) {
        console.error('[NotificationsAPI] Error fetching unread count:', error);
        return 0;
    }

    return count || 0;
}

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, error: Error}>}
 */
export async function markAsRead(notificationId) {
    const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);

    if (error) {
        console.error('[NotificationsAPI] Error marking as read:', error);
        return { success: false, error };
    }

    return { success: true, error: null };
}

/**
 * Mark all notifications as read for current user
 * @returns {Promise<{success: boolean, error: Error}>}
 */
export async function markAllAsRead() {
    const userId = auth.user?.id;
    if (!userId) return { success: false, error: new Error('No user') };

    const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false);

    if (error) {
        console.error('[NotificationsAPI] Error marking all as read:', error);
        return { success: false, error };
    }

    return { success: true, error: null };
}

